import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { pageTransition } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  getAgent,
  listAgentRuns,
  listAgentNotifications,
  triggerAgentRun,
  updateAgentConfig,
  type AgentRead,
  type AgentRunRead,
  type AgentNotificationRead,
} from "@/api/agents";
import {
  agentIcon,
  agentScheduleLabel,
  runStatusLabel,
  runStatusVariant,
  severityDotClass,
  severityVariant,
  timeAgo,
} from "@/components/agents/agentMeta";
import { Play, ChevronLeft } from "lucide-react";

export function AgentDetailPage() {
  const { name = "" } = useParams();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [agent, setAgent] = useState<AgentRead | null>(null);
  const [runs, setRuns] = useState<AgentRunRead[]>([]);
  const [notifications, setNotifications] = useState<AgentNotificationRead[]>([]);
  const [triggering, setTriggering] = useState(false);
  const [cronDraft, setCronDraft] = useState<string>("");

  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const refresh = async () => {
    try {
      const [a, r, n] = await Promise.all([
        getAgent(name),
        listAgentRuns({ agent_name: name, limit: 50 }),
        listAgentNotifications({ limit: 10 }),
      ]);
      setAgent(a);
      setRuns(r);
      setNotifications(n.filter((x) => x.agent_name === name));
      setCronDraft(a.schedule_cron ?? "");
    } catch {
      addToast("error", "Failed to load agent");
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const runNow = async () => {
    setTriggering(true);
    try {
      await triggerAgentRun(name);
      addToast("success", `${name} run queued`);
      setTimeout(refresh, 1500);
    } catch (err) {
      // Surface the real server error so it's actionable. The most
      // common failure for Pilot is the 400 "Pilot must be triggered
      // for a specific employee" — the previous opaque toast made
      // that look like a server outage when it was really "wrong
      // entry point, use Start Onboarding on the profile".
      const resp = (err as { response?: { data?: { detail?: unknown } } })
        ?.response;
      const detail = resp?.data?.detail;
      let msg = `Failed to trigger ${name} run`;
      if (typeof detail === "string" && detail) {
        msg = detail;
      } else if (Array.isArray(detail)) {
        const first = detail[0] as { msg?: string } | undefined;
        if (first?.msg) msg = first.msg;
      } else if (err instanceof Error && err.message) {
        msg = err.message;
      }
      addToast("error", msg);
    } finally {
      setTriggering(false);
    }
  };

  const toggleEnabled = async () => {
    if (!agent) return;
    try {
      await updateAgentConfig(name, { enabled: !agent.enabled });
      addToast("success", `${name} ${!agent.enabled ? "enabled" : "disabled"}`);
      refresh();
    } catch {
      addToast("error", "Failed to update agent");
    }
  };

  const saveCron = async () => {
    try {
      await updateAgentConfig(name, { schedule_cron: cronDraft || null });
      addToast("success", "Schedule updated");
      refresh();
    } catch {
      addToast("error", "Failed to update schedule");
    }
  };

  const latestNotification = notifications[0] ?? null;
  const Icon = agentIcon(name);

  const duration = (r: AgentRunRead): string => {
    if (!r.completed_at) return "—";
    const ms =
      new Date(r.completed_at).getTime() - new Date(r.started_at).getTime();
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <motion.div {...pageTransition}>
      <Link
        to="/agents"
        className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-4"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        All agents
      </Link>

      {!agent ? (
        <Skeleton className="h-32 rounded-xl mb-6" />
      ) : (
        <Card className="mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-accent/10">
                <Icon className="h-6 w-6 text-accent" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-2xl font-bold font-heading text-text-primary">
                  {agent.display_name}
                </h2>
                <p className="text-sm text-text-muted mt-1 max-w-2xl">
                  {agent.description}
                </p>
                <p className="text-xs text-text-muted mt-2">
                  {agentScheduleLabel(agent.name, agent.schedule_cron)}
                  {" · v"}
                  {agent.version}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`inline-flex items-center gap-2 text-xs ${
                  agent.enabled ? "text-success" : "text-text-muted"
                }`}
              >
                <span
                  className={`h-2 w-2 rounded-full ${
                    agent.enabled ? "bg-success" : "bg-text-muted/40"
                  }`}
                />
                {agent.enabled ? "Enabled" : "Disabled"}
              </span>
              {/* Pilot is the one agent that backend rejects on a
                  bare ``/run`` — it MUST carry ``new_employee_id``
                  in config so it targets a specific hire (Pilot
                  never mass-sends). Render the button disabled
                  with an explanatory tooltip + deep-link to
                  /people instead of letting the user click into a
                  guaranteed 400. */}
              {name === "pilot" ? (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    disabled
                    title="Pilot runs per-employee. Open a new hire's profile and click Start Onboarding."
                  >
                    <Play className="h-3.5 w-3.5" aria-hidden="true" />
                    Run now
                  </Button>
                  <Link
                    to="/employees"
                    className="text-xs text-accent hover:underline"
                  >
                    Go to People →
                  </Link>
                </div>
              ) : (
                <Button
                  size="sm"
                  isLoading={triggering}
                  onClick={runNow}
                >
                  <Play className="h-3.5 w-3.5" aria-hidden="true" />
                  Run now
                </Button>
              )}
            </div>
          </div>

          {isSuperAdmin && (
            <div className="mt-4 pt-4 border-t border-border flex flex-wrap items-end gap-3">
              <Button size="sm" variant="secondary" onClick={toggleEnabled}>
                {agent.enabled ? "Disable" : "Enable"}
              </Button>
              {agent.default_cron !== null && (
                <div className="flex items-end gap-2">
                  <div>
                    <label className="block text-xs text-text-muted mb-1">
                      Schedule (cron)
                    </label>
                    <input
                      type="text"
                      value={cronDraft}
                      placeholder={agent.default_cron ?? ""}
                      onChange={(e) => setCronDraft(e.target.value)}
                      className="px-3 py-1.5 rounded-md border border-border bg-background text-sm font-mono w-48"
                    />
                  </div>
                  <Button size="sm" variant="secondary" onClick={saveCron}>
                    Save
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Latest output */}
      {latestNotification && (
        <Card className="mb-6">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-semibold font-heading text-text-primary">
              Latest output
            </h3>
            <Badge variant={severityVariant(latestNotification.severity)}>
              {latestNotification.severity}
            </Badge>
          </div>
          <p className="text-sm font-medium text-text-primary mb-2">
            {latestNotification.title}
          </p>
          <p className="text-sm text-text-muted mb-4">
            {latestNotification.summary}
          </p>
          <AgentOutputRenderer
            agentName={name}
            content={latestNotification.full_content}
          />
        </Card>
      )}

      {/* Run history */}
      <Card>
        <h3 className="text-lg font-semibold font-heading text-text-primary mb-4">
          Run history
        </h3>
        {runs.length === 0 ? (
          <p className="text-sm text-text-muted py-6 text-center">
            No runs yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-text-muted text-left border-b border-border">
                  <th className="py-2 pr-4 font-medium">Started</th>
                  <th className="py-2 pr-4 font-medium">Trigger</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Duration</th>
                  <th className="py-2 pr-4 font-medium">Tokens</th>
                  <th className="py-2 pr-4 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-border/50 last:border-0"
                  >
                    <td className="py-2.5 pr-4 text-text-muted whitespace-nowrap">
                      {timeAgo(r.started_at)}
                    </td>
                    <td className="py-2.5 pr-4 text-text-muted capitalize">
                      {r.trigger_type}
                    </td>
                    <td className="py-2.5 pr-4">
                      <Badge variant={runStatusVariant(r.status)}>
                        {runStatusLabel(r.status, r.output_summary)}
                      </Badge>
                    </td>
                    <td className="py-2.5 pr-4 text-text-muted">{duration(r)}</td>
                    <td className="py-2.5 pr-4 text-text-muted">
                      {r.tokens_used || 0}
                    </td>
                    <td className="py-2.5 pr-4 text-text-muted">
                      ${Number(r.cost_usd || 0).toFixed(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </motion.div>
  );
}

function AgentOutputRenderer({
  agentName,
  content,
}: {
  agentName: string;
  content: Record<string, unknown> | null;
}) {
  if (!content) {
    return <p className="text-sm text-text-muted">No output payload.</p>;
  }

  if (agentName === "atlas") return <AtlasOutput content={content} />;
  if (agentName === "sentinel") return <SentinelOutput content={content} />;
  if (agentName === "pilot") return <PilotOutput content={content} />;

  return (
    <pre className="text-xs text-text-muted whitespace-pre-wrap bg-background p-3 rounded-md overflow-x-auto">
      {JSON.stringify(content, null, 2)}
    </pre>
  );
}

/**
 * Render one metric value. Atlas returns a mix of scalars and small shapes
 * (e.g. ``headcount: {current, change}``, ``pto_by_department: [...]``). For
 * the common ``{current, change}`` shape we surface the current value big
 * and a ±delta chip next to it; other objects/arrays are compacted so the
 * tile never shows raw JSON.
 */
function MetricValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-text-muted">—</span>;
  }
  if (typeof value === "number" || typeof value === "string") {
    return <>{String(value)}</>;
  }
  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    "current" in (value as Record<string, unknown>)
  ) {
    const v = value as { current: unknown; change?: unknown };
    const current = v.current;
    const change = typeof v.change === "number" ? v.change : undefined;
    return (
      <span className="inline-flex items-baseline gap-2">
        <span>
          {typeof current === "number" || typeof current === "string"
            ? String(current)
            : "—"}
        </span>
        {change !== undefined && (
          <span
            className={`text-xs font-medium px-1.5 py-0.5 rounded ${
              change > 0
                ? "bg-success/10 text-success"
                : change < 0
                ? "bg-danger/10 text-danger"
                : "bg-text-muted/10 text-text-muted"
            }`}
          >
            {change > 0 ? "+" : ""}
            {change}
          </span>
        )}
      </span>
    );
  }
  if (Array.isArray(value) && value.length === 0) {
    return <span className="text-text-muted">—</span>;
  }
  // Fallback: truncated JSON so long shapes don't blow out the tile.
  const s = JSON.stringify(value);
  return <span title={s}>{s.length > 40 ? `${s.slice(0, 37)}…` : s}</span>;
}

function AtlasOutput({ content }: { content: Record<string, unknown> }) {
  const metrics = (content.metrics as Record<string, unknown>) ?? {};
  const priorities = (content.priorities as Array<Record<string, unknown>>) ?? [];
  const briefing = (content.briefing as string) ?? (content.summary as string) ?? "";
  const metricEntries = Object.entries(metrics).slice(0, 8);

  return (
    <div className="space-y-4">
      {metricEntries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {metricEntries.map(([key, value]) => (
            <div
              key={key}
              className="rounded-lg border border-border p-3 bg-background"
            >
              <div className="text-xs text-text-muted capitalize">
                {key.replace(/_/g, " ")}
              </div>
              <div className="text-lg font-semibold text-text-primary mt-1">
                <MetricValue value={value} />
              </div>
            </div>
          ))}
        </div>
      )}
      {briefing && (
        <div className="prose prose-sm max-w-none text-sm text-text-primary whitespace-pre-wrap">
          {briefing}
        </div>
      )}
      {priorities.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-2">
            Priorities
          </h4>
          <ul className="space-y-2">
            {priorities.map((p, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-text-primary"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-accent mt-2 shrink-0" />
                <span>{(p.title as string) ?? (p.text as string) ?? JSON.stringify(p)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SentinelOutput({ content }: { content: Record<string, unknown> }) {
  // Multi-select category filter. Empty set = show all.
  const [activeCategories, setActiveCategories] = useState<Set<string>>(
    new Set()
  );

  const findings =
    (content.findings as Array<Record<string, unknown>>) ??
    (content.items as Array<Record<string, unknown>>) ??
    [];
  if (findings.length === 0) {
    return (
      <p className="text-sm text-success">
        All clear — no compliance issues found.
      </p>
    );
  }
  // Sort by severity so critical > warning > info in the rendered list.
  const severityRank: Record<string, number> = {
    critical: 0,
    warning: 1,
    info: 2,
  };
  const sorted = [...findings].sort(
    (a, b) =>
      (severityRank[(a.severity as string) ?? "info"] ?? 3) -
      (severityRank[(b.severity as string) ?? "info"] ?? 3)
  );

  // Tally by category for the summary strip.
  const byCategory = sorted.reduce<Record<string, number>>((acc, f) => {
    const cat = (f.category as string) || "other";
    acc[cat] = (acc[cat] ?? 0) + 1;
    return acc;
  }, {});
  const categoryEntries = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);

  const toggleCategory = (cat: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const visible =
    activeCategories.size === 0
      ? sorted
      : sorted.filter((f) =>
          activeCategories.has((f.category as string) || "other")
        );

  const allActive = activeCategories.size === 0;
  const pillBase =
    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors cursor-pointer select-none";
  const activeClass = "bg-accent text-white border border-accent";
  const inactiveClass =
    "bg-background border border-border text-text-primary hover:bg-border/40";

  return (
    <div className="space-y-3">
      {categoryEntries.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveCategories(new Set())}
            className={`${pillBase} ${allActive ? activeClass : inactiveClass}`}
          >
            <span>All</span>
            <span className={allActive ? "text-white/80" : "text-text-muted"}>
              · {sorted.length}
            </span>
          </button>
          {categoryEntries.map(([cat, count]) => {
            const isActive = activeCategories.has(cat);
            return (
              <button
                type="button"
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={`${pillBase} ${isActive ? activeClass : inactiveClass}`}
              >
                <span className="capitalize">{cat.replace(/_/g, " ")}</span>
                <span className={isActive ? "text-white/80" : "text-text-muted"}>
                  · {count}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <ul className="space-y-2">
        {visible.map((f, i) => {
          const severity = ((f.severity as string) ?? "info") as
            | "info"
            | "warning"
            | "critical";
          const detail =
            (f.detail as string) ??
            (f.title as string) ??
            (f.message as string) ??
            "";
          const employeeName = f.employee_name as string | undefined;
          const department = f.department as string | undefined;
          const category = f.category as string | undefined;
          const action =
            (f.recommended_action as string) ??
            (f.suggested_action as string) ??
            "";

          return (
            <li
              key={i}
              className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0"
            >
              <span
                className={`h-2 w-2 rounded-full mt-2 shrink-0 ${severityDotClass(
                  severity
                )}`}
              />
              <div className="flex-1 min-w-0">
                {(employeeName || department) && (
                  <p className="text-sm">
                    {employeeName && (
                      <span className="font-medium text-text-primary">
                        {employeeName}
                      </span>
                    )}
                    {department && (
                      <span className="text-text-muted">
                        {employeeName ? " · " : ""}
                        {department}
                      </span>
                    )}
                  </p>
                )}
                {detail && (
                  <p className="text-sm text-text-primary">{detail}</p>
                )}
                {action && (
                  <p className="text-xs text-text-muted mt-0.5">{action}</p>
                )}
                {category && (
                  <p className="text-xs text-text-muted mt-0.5 capitalize">
                    {category.replace(/_/g, " ")}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PilotOutput({ content }: { content: Record<string, unknown> }) {
  const actions = (content.actions as Array<Record<string, unknown>>) ?? [];
  const employeeName = (content.employee_name as string) ?? "New hire";
  const summary = (content.summary as string) ?? "";

  return (
    <div className="space-y-3">
      <p className="text-sm text-text-primary">
        <span className="font-medium">{employeeName}</span>
        {summary && ` — ${summary}`}
      </p>
      {actions.length > 0 && (
        <ul className="space-y-1.5">
          {actions.map((a, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-sm text-text-primary"
            >
              <span className="text-success mt-0.5">✓</span>
              <span>
                {(a.label as string) ?? (a.title as string) ?? JSON.stringify(a)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
